<template>
  <div v-loading="loading" class="nx-page nx-report-page kanban-truck-efficiency">
    <nx-report-header title="汽运出入库车辆运输效能榜">
      <div class="kb-header-actions">
        <el-button size="small" icon="el-icon-refresh" @click="loadData">刷新</el-button>
      </div>
    </nx-report-header>

    <el-form :inline="true" class="kb-filter-bar" size="small" @submit.native.prevent>
      <el-form-item label="日期范围">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始"
          end-placeholder="结束"
          value-format="yyyy-MM-dd"
          style="width: 260px"
          @change="loadData"
        />
      </el-form-item>
      <el-form-item label="业务类型">
        <el-select v-model="businessType" clearable placeholder="汽运出+入" style="width: 150px" @change="loadData">
          <el-option label="汽运出库" :value="2" />
          <el-option label="汽运入库" :value="3" />
        </el-select>
      </el-form-item>
      <el-form-item label="排行维度">
        <el-select v-model="groupBy" style="width: 140px" @change="loadData">
          <el-option label="按车牌" value="plate" />
          <el-option label="按司机" value="driver" />
        </el-select>
      </el-form-item>
      <el-form-item label="排行指标">
        <el-select v-model="metric" style="width: 200px" @change="loadData">
          <el-option label="总净重（吨）" value="tons" />
          <el-option label="完成车次" value="trips" />
          <el-option label="平均在厂（越短越好）" value="stay" />
          <el-option label="单次均净重（吨/车）" value="tonPerTrip" />
        </el-select>
      </el-form-item>
    </el-form>

    <div class="kpi-row">
      <div class="kpi-card">
        <div class="lab">完成车次</div>
        <div class="val">
          {{ formatInt(summary.totalTrips) }}<span class="unit">笔</span>
        </div>
      </div>
      <div class="kpi-card">
        <div class="lab">总净重</div>
        <div class="val">
          {{ formatTons(summary.totalNetWeightTons) }}<span class="unit">吨</span>
        </div>
      </div>
      <div class="kpi-card">
        <div class="lab">平均在厂（全场）</div>
        <div class="val">
          {{ formatStayMin(summary.avgPlantStaySeconds) }}<span class="unit">分钟</span>
        </div>
      </div>
      <div class="kpi-card">
        <div class="lab">不重复车牌</div>
        <div class="val">
          {{ formatInt(summary.distinctPlateCount) }}<span class="unit">辆</span>
        </div>
      </div>
    </div>

    <div class="layout">
      <div class="chart-card">
        <h3>TOP 排行（{{ metricLabel }}）· {{ rangeText }}</h3>
        <div ref="chartRef" class="chart-box" />
      </div>
      <div class="side">
        <div class="podium">
          <h4>前三名</h4>
          <template v-if="topThree.length">
            <div v-for="(row, idx) in topThree" :key="idx" :class="'medal r' + (idx + 1)">
              <div class="rk">{{ ['🥇', '🥈', '🥉'][idx] }}</div>
              <div style="flex: 1; min-width: 0">
                <div class="nm" :title="row.dimensionKey">{{ row.dimensionKey }}</div>
                <div class="sub">{{ podiumLine(row) }}</div>
              </div>
            </div>
          </template>
          <div v-else style="color: #94a3b8; font-size: 13px">暂无数据</div>
        </div>
      </div>
    </div>

    <div class="detail-card">
      <h4>明细（前 {{ items.length }} 条）</h4>
      <el-table :data="items" border stripe size="small" style="width: 100%">
        <el-table-column type="index" label="#" width="48" :index="tableIndex" />
        <el-table-column prop="dimensionKey" :label="groupBy === 'driver' ? '司机' : '车牌'" min-width="120" />
        <el-table-column prop="tripCount" label="车次" width="72" align="right" />
        <el-table-column prop="totalNetWeight" label="总净重(吨)" width="110" align="right">
          <template slot-scope="{ row }">{{ row.totalNetWeight != null ? row.totalNetWeight.toFixed(2) : '—' }}</template>
        </el-table-column>
        <el-table-column label="平均在厂" width="100" align="right">
          <template slot-scope="{ row }">{{ formatStayMin(row.avgPlantStaySeconds) }}</template>
        </el-table-column>
        <el-table-column label="均吨/车" width="90" align="right">
          <template slot-scope="{ row }">{{
            row.avgTonPerTrip != null ? row.avgTonPerTrip.toFixed(2) : '—'
          }}</template>
        </el-table-column>
        <el-table-column prop="sampleCustName" label="样例货主" min-width="120" show-overflow-tooltip />
      </el-table>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { fetchTruckEfficiencyLeaderboard } from './api'

function monthStartStr() {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  return `${y}-${m}-01`
}

function todayStr() {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export default {
  name: 'KanbanTruckEfficiencyLeaderboard',
  components: { NxReportHeader },
  data() {
    return {
      loading: false,
      dateRange: [monthStartStr(), todayStr()],
      businessType: undefined,
      groupBy: 'plate',
      metric: 'tons',
      result: {
        startDate: null,
        endDate: null,
        summary: {},
        items: []
      },
      chartInstance: null
    }
  },
  computed: {
    summary() {
      return this.result.summary || {}
    },
    items() {
      return this.result.items || []
    },
    topThree() {
      return this.items.slice(0, 3)
    },
    metricLabel() {
      const m = {
        tons: '总净重',
        trips: '完成车次',
        stay: '平均在厂',
        tonPerTrip: '单次均净重'
      }
      return m[this.metric] || '总净重'
    },
    rangeText() {
      if (this.result.startDate && this.result.endDate) {
        return `${this.result.startDate} ~ ${this.result.endDate}`
      }
      return ''
    }
  },
  mounted() {
    this.loadData()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
  },
  methods: {
    tableIndex(index) {
      return index + 1
    },
    formatInt(v) {
      if (v == null || v === '') return '—'
      return Number(v).toLocaleString('zh-CN')
    },
    formatTons(v) {
      if (v == null || v === '') return '—'
      const n = Number(v)
      if (!Number.isFinite(n)) return '—'
      return n.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
    },
    formatStayMin(sec) {
      if (sec == null || sec === '') return '—'
      const m = Number(sec) / 60
      if (!Number.isFinite(m)) return '—'
      return m < 10 ? m.toFixed(1) : Math.round(m).toString()
    },
    podiumLine(row) {
      const p = []
      p.push(`${row.tripCount} 次`)
      p.push(`${row.totalNetWeight != null ? row.totalNetWeight.toFixed(1) : '—'} 吨`)
      const sm = this.formatStayMin(row.avgPlantStaySeconds)
      if (sm !== '—') p.push(`均厂 ${sm} 分`)
      return p.join(' · ')
    },
    barColor(i) {
      if (i === 0) {
        return new echarts.graphic.LinearGradient(0, 0, 1, 0, [
          { offset: 0, color: '#b45309' },
          { offset: 1, color: '#fbbf24' }
        ])
      }
      if (i === 1) {
        return new echarts.graphic.LinearGradient(0, 0, 1, 0, [
          { offset: 0, color: '#475569' },
          { offset: 1, color: '#94a3b8' }
        ])
      }
      if (i === 2) {
        return new echarts.graphic.LinearGradient(0, 0, 1, 0, [
          { offset: 0, color: '#a16207' },
          { offset: 1, color: '#eab308' }
        ])
      }
      const greens = ['#047857', '#059669', '#10b981', '#34d399', '#6ee7b7', '#34d399', '#10b981']
      return greens[i % greens.length]
    },
    chartBarValue(row) {
      const v = row.rankValue
      if (v != null && Number.isFinite(Number(v))) return Number(v)
      if (this.metric === 'trips') return row.tripCount || 0
      if (this.metric === 'tons') return row.totalNetWeight || 0
      if (this.metric === 'stay') return row.avgPlantStaySeconds != null ? row.avgPlantStaySeconds : 0
      if (this.metric === 'tonPerTrip') return row.avgTonPerTrip != null ? row.avgTonPerTrip : 0
      return 0
    },
    labelFormatter(val) {
      if (this.metric === 'stay') {
        const min = Number(val) / 60
        return (Number.isFinite(min) ? min.toFixed(1) : '0') + ' 分'
      }
      if (this.metric === 'trips') return String(Math.round(val))
      if (this.metric === 'tonPerTrip') return Number(val).toFixed(2)
      return Number(val).toLocaleString('zh-CN', { maximumFractionDigits: 0 })
    },
    onResize() {
      this.chartInstance && this.chartInstance.resize()
    },
    async loadData() {
      const dr = this.dateRange
      const startDate = dr && dr[0] ? dr[0] : monthStartStr()
      const endDate = dr && dr[1] ? dr[1] : todayStr()
      this.loading = true
      try {
        const data = await fetchTruckEfficiencyLeaderboard({
          startDate,
          endDate,
          businessType: this.businessType,
          groupBy: this.groupBy,
          metric: this.metric,
          limit: 20
        })
        this.result = data
        this.$nextTick(() => this.renderChart())
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    renderChart() {
      const el = this.$refs.chartRef
      if (!el) return
      const list = this.items
      if (!this.chartInstance) this.chartInstance = echarts.init(el)
      if (!list.length) {
        this.chartInstance.setOption({
          title: { text: '暂无数据', left: 'center', top: 'middle', textStyle: { color: '#94a3b8', fontSize: 14 } },
          xAxis: { show: false },
          yAxis: { show: false },
          series: []
        })
        return
      }
      const names = list.map((r) => r.dimensionKey)
      const vals = list.map((r) => this.chartBarValue(r))
      const isStay = this.metric === 'stay'
      this.chartInstance.setOption({
        title: { show: false },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            const p = params[0]
            const row = list[p.dataIndex]
            let s = row.dimensionKey + '<br/>'
            s += '车次：<b>' + row.tripCount + '</b><br/>'
            s += '总净重：<b>' + (row.totalNetWeight != null ? row.totalNetWeight.toFixed(2) : '—') + '</b> 吨<br/>'
            s +=
              '平均在厂：<b>' +
              (row.avgPlantStaySeconds != null ? this.formatStayMin(row.avgPlantStaySeconds) : '—') +
              '</b> 分钟<br/>'
            s += this.metricLabel + '：<b>' + this.labelFormatter(this.chartBarValue(row)) + '</b>'
            return s
          }
        },
        grid: { left: 8, right: 56, top: 12, bottom: 8, containLabel: true },
        xAxis: {
          type: 'value',
          max: (v) => Math.ceil(v.max * 1.06),
          splitLine: { lineStyle: { type: 'dashed', color: '#e2e8f0' } },
          axisLabel: {
            formatter: (v) => this.labelFormatter(v),
            color: '#64748b',
            fontSize: 11
          }
        },
        yAxis: {
          type: 'category',
          data: names,
          inverse: true,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '#334155', fontSize: 12, width: 120, overflow: 'truncate' }
        },
        series: [
          {
            type: 'bar',
            realtimeSort: !isStay,
            data: vals.map((v, i) => ({
              value: v,
              itemStyle: { color: this.barColor(i), borderRadius: [0, 8, 8, 0] }
            })),
            barWidth: '55%',
            label: {
              show: true,
              position: 'right',
              formatter: (p) => this.labelFormatter(p.value),
              color: '#475569',
              fontSize: 11,
              fontWeight: 600
            }
          }
        ],
        animationDurationUpdate: 600
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
